import { Controller } from "@hotwired/stimulus"

export default class extends Controller {
  static targets = ["editor", "hiddenField", "mergeDropdown", "imagePopover", "imageUrlInput"]
  static values = {
    mergeVariables: { type: Array, default: [] }
  }

  connect() {
    this.editorTarget.addEventListener("input", () => this._syncHidden())
    this.editorTarget.addEventListener("paste", (e) => this._handlePaste(e))
    this.editorTarget.addEventListener("keydown", (e) => this._handleKeydown(e))
    this._syncHidden()
  }

  // --- Toolbar actions ---

  bold(e) {
    e.preventDefault()
    document.execCommand("bold", false)
    this.editorTarget.focus()
  }

  italic(e) {
    e.preventDefault()
    document.execCommand("italic", false)
    this.editorTarget.focus()
  }

  underline(e) {
    e.preventDefault()
    document.execCommand("underline", false)
    this.editorTarget.focus()
  }

  insertLink(e) {
    e.preventDefault()
    const url = prompt("Enter URL:")
    if (url) {
      const selection = window.getSelection()
      if (selection.rangeCount > 0 && !selection.isCollapsed) {
        document.execCommand("createLink", false, url)
        // Style the link for email safety
        const links = this.editorTarget.querySelectorAll("a")
        links.forEach(a => {
          if (a.href === url || a.getAttribute("href") === url) {
            a.style.color = "#1a73e8"
          }
        })
      } else {
        // No selection: insert link text
        const a = document.createElement("a")
        a.href = url
        a.style.color = "#1a73e8"
        a.textContent = url
        this._insertAtCursor(a)
      }
    }
    this.editorTarget.focus()
    this._syncHidden()
  }

  bulletList(e) {
    e.preventDefault()
    document.execCommand("insertUnorderedList", false)
    this.editorTarget.focus()
  }

  showImagePopover(e) {
    e.preventDefault()
    if (this.hasImagePopoverTarget) {
      this.imagePopoverTarget.classList.toggle('hidden')
      if (!this.imagePopoverTarget.classList.contains('hidden') && this.hasImageUrlInputTarget) {
        this.imageUrlInputTarget.value = ''
        this.imageUrlInputTarget.focus()
      }
    }
  }

  insertImage(e) {
    e.preventDefault()
    if (!this.hasImageUrlInputTarget) return

    const url = this.imageUrlInputTarget.value.trim()
    if (!url) return

    this.editorTarget.focus()

    // Restore selection if we lost it when clicking the popover
    const sel = window.getSelection()
    if (sel.rangeCount === 0) {
      // Place cursor at end if no selection
      const range = document.createRange()
      range.selectNodeContents(this.editorTarget)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    }

    // Insert the image with email-safe inline styles
    document.execCommand('insertHTML', false,
      `<img src="${url}" style="max-width: 100%; height: auto; display: block; margin: 8px 0;" />`
    )

    // Sync to hidden field
    this._syncHidden()

    // Close popover
    if (this.hasImagePopoverTarget) {
      this.imagePopoverTarget.classList.add('hidden')
    }
  }

  toggleMergeDropdown(e) {
    e.preventDefault()
    this.mergeDropdownTarget.classList.toggle("hidden")
  }

  insertMergeVariable(e) {
    e.preventDefault()
    const value = e.currentTarget.dataset.value
    if (!value) return

    const textNode = document.createTextNode(value)
    this._insertAtCursor(textNode)
    this.mergeDropdownTarget.classList.add("hidden")
    this.editorTarget.focus()
    this._syncHidden()
  }

  closeMergeDropdown(e) {
    if (!this.hasMergeDropdownTarget) return
    if (!this.mergeDropdownTarget.contains(e.target)) {
      this.mergeDropdownTarget.classList.add("hidden")
    }
  }

  // --- Paste handling ---

  _handlePaste(e) {
    e.preventDefault()
    const html = e.clipboardData.getData("text/html")
    const text = e.clipboardData.getData("text/plain")

    if (html) {
      // Strip complex formatting, keep basic inline styles
      const cleaned = this._cleanHtml(html)
      document.execCommand("insertHTML", false, cleaned)
    } else {
      document.execCommand("insertText", false, text)
    }
    this._syncHidden()
  }

  _cleanHtml(html) {
    const doc = new DOMParser().parseFromString(html, "text/html")
    const body = doc.body

    // Remove all class, data, and id attributes
    body.querySelectorAll("*").forEach(el => {
      const attrs = Array.from(el.attributes)
      attrs.forEach(attr => {
        if (attr.name !== "href" && attr.name !== "style") {
          el.removeAttribute(attr.name)
        }
      })
      // Strip non-inline styles
      if (el.style) {
        const allowed = ["font-weight", "font-style", "text-decoration", "color"]
        const style = el.style
        const kept = {}
        allowed.forEach(prop => {
          if (style[prop]) kept[prop] = style[prop]
        })
        el.removeAttribute("style")
        Object.entries(kept).forEach(([k, v]) => { el.style[k] = v })
      }
    })

    // Convert divs to p tags
    body.querySelectorAll("div").forEach(div => {
      const p = doc.createElement("p")
      p.innerHTML = div.innerHTML
      div.replaceWith(p)
    })

    return body.innerHTML
  }

  // --- Keyboard shortcuts ---

  _handleKeydown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === "b") {
      e.preventDefault()
      document.execCommand("bold", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "i") {
      e.preventDefault()
      document.execCommand("italic", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "u") {
      e.preventDefault()
      document.execCommand("underline", false)
    } else if ((e.metaKey || e.ctrlKey) && e.key === "k") {
      e.preventDefault()
      this.insertLink(e)
    }
    // Sync after any key
    setTimeout(() => this._syncHidden(), 0)
  }

  // --- Helpers ---

  _insertAtCursor(node) {
    const sel = window.getSelection()
    if (sel.rangeCount > 0) {
      const range = sel.getRangeAt(0)
      range.deleteContents()
      range.insertNode(node)
      range.collapse(false)
      sel.removeAllRanges()
      sel.addRange(range)
    } else {
      this.editorTarget.appendChild(node)
    }
    this._syncHidden()
  }

  _syncHidden() {
    if (this.hasHiddenFieldTarget) {
      this.hiddenFieldTarget.value = this.editorTarget.innerHTML
    }
  }
}
